/**
 * Local personal bests. `docs/DESIGN.md`'s owner amendment: "keep local
 * personal bests in localStorage keyed by mode id + config" — groundwork for
 * accounts later (`docs/DESIGN.md`, "Accounts": "per-mode personal bests,
 * keyed by mode + configuration"), so the key shape here is exactly the one
 * an account-backed store would also need.
 *
 * A plain module, not a `.svelte.ts` one — there is no reactive state here,
 * only a read/compare/write cycle `GameScreen.svelte` calls once per finished
 * run — so this is straightforwardly unit-testable the same way
 * `settings-io.ts` is (see that file's header comment for why the split
 * exists at all).
 */
import type { PersonalBestScore } from '@interlock/modes';

import { safeLocalStorage } from './settings-io.js';

export const PERSONAL_BESTS_STORAGE_KEY = 'interlock:personal-bests:v1';

export interface PersonalBestRecord {
  readonly value: number;
  readonly betterWhen: 'lower' | 'higher';
  readonly formatted: string;
  readonly achievedAt: number;
}

export type PersonalBestStore = Record<string, PersonalBestRecord>;

function readStore(storage: Storage | undefined): PersonalBestStore {
  if (storage === undefined) return {};
  try {
    const raw = storage.getItem(PERSONAL_BESTS_STORAGE_KEY);
    if (raw === null) return {};
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? (parsed as PersonalBestStore) : {};
  } catch {
    return {};
  }
}

function writeStore(store: PersonalBestStore, storage: Storage | undefined): void {
  if (storage === undefined) return;
  try {
    storage.setItem(PERSONAL_BESTS_STORAGE_KEY, JSON.stringify(store));
  } catch {
    // Storage full or blocked (private browsing) — same policy as settings: stop persisting, don't interrupt play.
  }
}

/** `storage` defaults to `localStorage`, injectable so this stays testable without a real browser. */
export function loadPersonalBest(
  key: string,
  storage: Storage | undefined = safeLocalStorage(),
): PersonalBestRecord | null {
  return readStore(storage)[key] ?? null;
}

/**
 * The whole local store, for the login-time merge.
 *
 * `docs/DESIGN.md`'s accounts amendment: "personal bests sync (merge local
 * bests up on login — keep the better result per key; server is source of
 * truth after)." This is the "local bests" half; the merge itself is the
 * server's, because it is the source of truth.
 */
export function loadAllPersonalBests(
  storage: Storage | undefined = safeLocalStorage(),
): PersonalBestStore {
  return readStore(storage);
}

/**
 * Take the merged set back down from the server.
 *
 * The local store is not *replaced*, it is merged into with the same
 * better-wins rule — and that matters for one case: a run finished while the
 * page was offline, after the last sync. Replacing would silently throw it
 * away; merging keeps it and it goes up on the next push.
 *
 * Signing out leaves all of this exactly where it is, which is the whole point
 * of the amendment's "guests keep localStorage exactly as today": a person who
 * signs out is a guest again, with their bests.
 */
export function mergePersonalBests(
  incoming: Iterable<{
    modeKey: string;
    value: number;
    betterWhen: 'lower' | 'higher';
    formatted: string;
    achievedAtMs: number;
  }>,
  storage: Storage | undefined = safeLocalStorage(),
): PersonalBestStore {
  const store = readStore(storage);
  let changed = false;
  for (const entry of incoming) {
    const existing = store[entry.modeKey] ?? null;
    if (!isBetter(entry, existing)) continue;
    store[entry.modeKey] = {
      value: entry.value,
      betterWhen: entry.betterWhen,
      formatted: entry.formatted,
      achievedAt: entry.achievedAtMs,
    };
    changed = true;
  }
  if (changed) writeStore(store, storage);
  return store;
}

/** True when `score` would improve on `existing` (or there is nothing recorded yet). */
export function isBetter(score: PersonalBestScore, existing: PersonalBestRecord | null): boolean {
  if (existing === null) return true;
  return score.betterWhen === 'lower' ? score.value < existing.value : score.value > existing.value;
}

/**
 * Record a finished run's score under `key` (`@interlock/modes`'
 * `encodeModeConfig`) if it improves on whatever is already there. Always
 * returns the best now on record — the caller (`GameScreen.svelte`) shows it
 * either way, "new best" or not.
 */
export function recordPersonalBest(
  key: string,
  score: PersonalBestScore,
  storage: Storage | undefined = safeLocalStorage(),
  now: number = Date.now(),
): { best: PersonalBestRecord; isNewBest: boolean } {
  const store = readStore(storage);
  const existing = store[key] ?? null;

  if (existing !== null && !isBetter(score, existing)) {
    return { best: existing, isNewBest: false };
  }

  const best: PersonalBestRecord = {
    value: score.value,
    betterWhen: score.betterWhen,
    formatted: score.formatted,
    achievedAt: now,
  };
  store[key] = best;
  writeStore(store, storage);
  return { best, isNewBest: true };
}
